import { Link } from "react-router-dom";
import { useSEO, breadcrumb } from "../lib/seo";
import { SITE } from "../lib/site";
import PageHero from "../components/PageHero";
import { ExternalLink, ShieldCheck, CheckCircle2, AlertTriangle, RefreshCcw } from "lucide-react";

const TIPS = [
  "Selalu rujuk halaman ini untuk daftar terbaru.",
  "Bookmark URL resmi untuk akses berikutnya.",
  "Jangan menggunakan link dari sumber tidak dikenal.",
  "Hindari membagikan link alternatif tanpa konteks.",
];

export default function AlternativeLinks() {
  useSEO({
    title: "Link Alternatif Sloto69 | Akses Resmi Terbaru",
    description: "Halaman link alternatif Sloto69 untuk membantu pengguna mengakses halaman resmi saat link utama mengalami kendala.",
    keywords: "sloto69 link alternatif, link resmi sloto69, akses sloto69",
    canonical: `${SITE.baseUrl}/link-alternatif`,
    jsonLd: breadcrumb([
      { name: "Beranda", url: `${SITE.baseUrl}/` },
      { name: "Link Alternatif", url: `${SITE.baseUrl}/link-alternatif` },
    ]),
  });

  return (
    <>
      <PageHero
        eyebrow="Link Alternatif"
        title="Akses Link Resmi Sloto69 dengan Tenang dan Terarah"
        description="Halaman ini menampilkan referensi tautan resmi Sloto69. Selalu kunjungi sumber resmi untuk mendapatkan informasi yang tepat."
        breadcrumb={[{ label: "Link Alternatif" }]}
      />

      <section className="section-pad" data-testid="alt-links">
        <div className="shell grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="gold-pill">Tautan Utama</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Akses Resmi Sloto69</h2>
            <p className="mt-4 text-slate-300">
              Gunakan tautan resmi di bawah ini sebagai akses utama. Kami akan memperbarui daftar
              ini apabila terjadi perubahan untuk memastikan kenyamanan pengguna.
            </p>

            <div className="mt-6 grid gap-3">
              {[
                { label: "Akses Login Sloto69", href: SITE.loginUrl, testid: "alt-login" },
                { label: "Akses Daftar Sloto69", href: SITE.registerUrl, testid: "alt-daftar" },
                { label: "Halaman Resmi Utama", href: SITE.alternativeUrl, testid: "alt-utama" },
              ].map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  data-testid={l.testid}
                  className="card-premium p-5 flex items-center justify-between hover:border-[#10B981]/40"
                >
                  <span className="font-medium">{l.label}</span>
                  <ExternalLink className="w-4 h-4 text-[#10B981]" />
                </a>
              ))}
            </div>

            <p className="mt-6 text-xs text-slate-500 leading-relaxed">
              Catatan: tautan di atas adalah placeholder pada website company profile ini.
              Silakan gantikan dengan link resmi Anda saat melakukan integrasi.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="card-premium p-7">
              <RefreshCcw className="w-8 h-8 text-[#D4AF37]" />
              <h3 className="mt-4 font-display text-2xl font-semibold">Mengapa Membutuhkan Link Alternatif</h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                Link alternatif berperan penting saat link utama mengalami kendala teknis,
                seperti pemeliharaan rutin, hambatan jaringan, atau kebijakan ISP. Dengan
                adanya tautan resmi cadangan, pengguna tetap dapat mengakses informasi
                tanpa perlu mencari di sumber tidak dapat dipertanggungjawabkan.
              </p>

              <h4 className="mt-6 font-display text-lg font-semibold">Tips Penggunaan Link Alternatif</h4>
              <ul className="mt-3 space-y-2.5 text-sm text-slate-300">
                {TIPS.map((t, i) => (
                  <li key={t} data-testid={`alt-tip-${i}`} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#10B981] flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-premium p-7 mt-6 flex gap-4">
              <AlertTriangle className="w-8 h-8 text-[#D4AF37] flex-shrink-0" />
              <div>
                <h3 className="font-display text-xl font-semibold">Hindari Tautan Tidak Resmi</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  Banyak pihak menyebarkan tautan menyerupai resmi melalui media sosial atau
                  pesan singkat. Hindari klik tautan dari sumber tidak dikenal dan rujuk
                  selalu halaman ini sebagai sumber resmi Anda.
                </p>
                <Link to="/keamanan" data-testid="alt-keamanan-link" className="btn-secondary mt-4 inline-flex">
                  <ShieldCheck className="w-4 h-4" /> Tips Keamanan
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
